import { useState, useEffect, useCallback, useRef } from 'react';
import echo from '@/lib/echo';
import axios from 'axios';

export interface DebugState {
    isDebugMode: boolean;
    isDebugging: boolean;
    isPaused: boolean;
    runId: string | null;
    currentNode: string | null;
    context: Record<string, unknown>;
    breakpoints: Set<string>;
    history: Array<{
        nodeId: string;
        nodeType: string;
        timestamp: string;
        action: 'entered' | 'paused' | 'completed';
    }>;
    error: string | null;
}

export interface TestCase {
    name: string;
    description?: string;
    mode: string;
}

interface UseWorkflowDebugOptions {
    /** Full capability name for test discovery (e.g., "workflow.casefile.ingest") */
    capabilityName: string;
    /** Workflow key for execution (e.g., "workflow.casefile.ingest" or database key) */
    workflowKey: string;
    onNodeEntered?: (nodeId: string) => void;
    onPaused?: (nodeId: string) => void;
    onCompleted?: () => void;
}

export function useWorkflowDebug({
    capabilityName,
    workflowKey,
    onNodeEntered,
    onPaused,
    onCompleted,
}: UseWorkflowDebugOptions) {
    const [state, setState] = useState<DebugState>({
        isDebugMode: false,
        isDebugging: false,
        isPaused: false,
        runId: null,
        currentNode: null,
        context: {},
        breakpoints: new Set(),
        history: [],
        error: null,
    });

    const [testCases, setTestCases] = useState<TestCase[]>([]);
    const [selectedTestCase, setSelectedTestCase] = useState<string | null>(null);
    const channelRef = useRef<ReturnType<typeof echo.private> | null>(null);

    // Fetch available test cases when entering debug mode
    // Uses the full capability name (e.g., "workflow.casefile.ingest") for test discovery
    useEffect(() => {
        if (state.isDebugMode && capabilityName) {
            const encodedName = encodeURIComponent(capabilityName);
            const url = `/capability-debug/${encodedName}/tests`;
            
            axios.get(url)
                .then((response) => {
                    setTestCases(response.data.test_cases || []);
                })
                .catch((error) => {
                    console.error('[useWorkflowDebug] Failed to fetch test cases:', error);
                    setState((prev) => ({ ...prev, error: 'Failed to load test cases' }));
                });
        }
    }, [state.isDebugMode, capabilityName]);

    // Subscribe to WebSocket channel when debugging starts
    useEffect(() => {
        if (!state.runId) {
            return;
        }

        const channel = echo.private(`workflow.${state.runId}.debug`);
        channelRef.current = channel;

        channel.listen('.debug.paused', (raw: unknown) => {
            const event = raw as {
                node_id: string;
                node_type: string;
                context?: Record<string, unknown>;
                timestamp: string;
            };
            console.log('[Debug] Paused at:', event.node_id);

            setState((prev) => ({
                ...prev,
                isPaused: true,
                currentNode: event.node_id,
                context: event.context || prev.context,
                history: [
                    ...prev.history,
                    {
                        nodeId: event.node_id,
                        nodeType: event.node_type,
                        timestamp: event.timestamp,
                        action: 'paused',
                    },
                ],
            }));

            onPaused?.(event.node_id);
        });

        channel.listen('.debug.resumed', (raw: unknown) => {
            const event = raw as { resume_mode: string };
            console.log('[Debug] Resumed:', event.resume_mode);

            setState((prev) => ({
                ...prev,
                isPaused: false,
            }));
        });

        channel.listen('.debug.completed', (raw: unknown) => {
            const event = raw as { status: string; timestamp: string };
            console.log('[Debug] Completed:', event.status);

            setState((prev) => ({
                ...prev,
                isDebugging: false,
                isPaused: false,
                history: [
                    ...prev.history,
                    {
                        nodeId: prev.currentNode || 'end',
                        nodeType: 'completion',
                        timestamp: event.timestamp,
                        action: 'completed',
                    },
                ],
            }));

            onCompleted?.();
        });

        channel.listen('.state.transitioned', (raw: unknown) => {
            const event = raw as {
                to_state: string;
                node_type: string;
                context?: Record<string, unknown>;
                timestamp: string;
            };
            console.log('[Debug] Transitioned to:', event.to_state);

            setState((prev) => ({
                ...prev,
                currentNode: event.to_state,
                context: event.context || prev.context,
                history: [
                    ...prev.history,
                    {
                        nodeId: event.to_state,
                        nodeType: event.node_type,
                        timestamp: event.timestamp,
                        action: 'entered',
                    },
                ],
            }));

            if (event.to_state) {
                onNodeEntered?.(event.to_state);
            }
        });

        return () => {
            channel.stopListening('.debug.paused');
            channel.stopListening('.debug.resumed');
            channel.stopListening('.debug.completed');
            channel.stopListening('.state.transitioned');
            echo.leave(`workflow.${state.runId}.debug`);
            channelRef.current = null;
        };
    }, [state.runId, onNodeEntered, onPaused, onCompleted]);

    // Toggle debug mode
    const toggleDebugMode = useCallback(() => {
        setState((prev) => ({
            ...prev,
            isDebugMode: !prev.isDebugMode,
            // Reset state when exiting debug mode
            ...(!prev.isDebugMode ? {} : {
                isDebugging: false,
                isPaused: false,
                runId: null,
                currentNode: null,
                history: [],
                error: null,
            }),
        }));
    }, []);

    // Toggle breakpoint on a node
    const toggleBreakpoint = useCallback((nodeId: string) => {
        setState((prev) => {
            const newBreakpoints = new Set(prev.breakpoints);
            if (newBreakpoints.has(nodeId)) {
                newBreakpoints.delete(nodeId);
            } else {
                newBreakpoints.add(nodeId);
            }

            // If debugging is active, update breakpoints on server
            if (prev.runId) {
                axios.put(`/workflow-debug/${prev.runId}/breakpoints`, {
                    breakpoints: Array.from(newBreakpoints),
                }).catch(console.error);
            }

            return { ...prev, breakpoints: newBreakpoints };
        });
    }, []);

    // Start debugging with selected test case
    const startDebug = useCallback(async () => {
        if (!selectedTestCase) {
            setState((prev) => ({ ...prev, error: 'Please select a test case' }));
            return;
        }

        setState((prev) => ({ ...prev, error: null }));

        try {
            const response = await axios.post('/workflow-debug/start', {
                workflow_key: workflowKey,
                test_case: selectedTestCase,
                breakpoints: Array.from(state.breakpoints),
            });

            setState((prev) => ({
                ...prev,
                isDebugging: true,
                isPaused: response.data.status === 'paused',
                runId: response.data.run_id,
                currentNode: response.data.current_node,
                context: response.data.context || {},
                history: [],
            }));
        } catch (error) {
            console.error('Failed to start debug session:', error);
            const axiosError = error as { response?: { data?: { error?: string } } };
            setState((prev) => ({
                ...prev,
                error: axiosError.response?.data?.error || 'Failed to start debug session',
            }));
        }
    }, [workflowKey, selectedTestCase, state.breakpoints]);

    // Step to next node
    const step = useCallback(async () => {
        if (!state.runId || !state.isPaused) return;

        try {
            await axios.post(`/workflow-debug/${state.runId}/step`);
            setState((prev) => ({ ...prev, isPaused: false }));
        } catch (error) {
            console.error('Failed to step:', error);
            const axiosError = error as { response?: { data?: { error?: string } } };
            setState((prev) => ({
                ...prev,
                error: axiosError.response?.data?.error || 'Failed to step',
            }));
        }
    }, [state.runId, state.isPaused]);

    // Continue to next breakpoint
    const continueExecution = useCallback(async () => {
        if (!state.runId || !state.isPaused) return;

        try {
            await axios.post(`/workflow-debug/${state.runId}/continue`);
            setState((prev) => ({ ...prev, isPaused: false }));
        } catch (error) {
            console.error('Failed to continue:', error);
            const axiosError = error as { response?: { data?: { error?: string } } };
            setState((prev) => ({
                ...prev,
                error: axiosError.response?.data?.error || 'Failed to continue',
            }));
        }
    }, [state.runId, state.isPaused]);

    // Stop debugging
    const stopDebug = useCallback(async () => {
        if (state.runId) {
            try {
                await axios.post(`/workflow-debug/${state.runId}/stop`);
            } catch (error) {
                console.error('Failed to stop cleanly:', error);
            }
        }

        setState((prev) => ({
            ...prev,
            isDebugging: false,
            isPaused: false,
            runId: null,
            currentNode: null,
            history: [],
        }));
    }, [state.runId]);

    // Keyboard shortcuts
    useEffect(() => {
        if (!state.isDebugMode) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // F5 - Continue
            if (e.key === 'F5' && !e.shiftKey) {
                e.preventDefault();
                if (state.isPaused) {
                    continueExecution();
                } else if (!state.isDebugging && selectedTestCase) {
                    startDebug();
                }
            }

            // Shift+F5 - Stop
            if (e.key === 'F5' && e.shiftKey) {
                e.preventDefault();
                if (state.isDebugging) {
                    stopDebug();
                }
            }

            // F10 - Step Over
            if (e.key === 'F10') {
                e.preventDefault();
                if (state.isPaused) {
                    step();
                }
            }

            // Escape - Exit debug mode
            if (e.key === 'Escape') {
                if (state.isDebugging) {
                    stopDebug();
                } else {
                    toggleDebugMode();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        state.isDebugMode,
        state.isDebugging,
        state.isPaused,
        selectedTestCase,
        startDebug,
        step,
        continueExecution,
        stopDebug,
        toggleDebugMode,
    ]);

    return {
        ...state,
        testCases,
        selectedTestCase,
        setSelectedTestCase,
        toggleDebugMode,
        toggleBreakpoint,
        startDebug,
        step,
        continueExecution,
        stopDebug,
    };
}

